import { useT } from '@/lib/i18n';
import { useEffect, useState } from 'react';

const STORE_KEY = 'pf-onboarding-done';

interface Step {
  icon: string;
  titleKey: 'onboarding.step1.title' | 'onboarding.step2.title' | 'onboarding.step3.title';
  bodyKey: 'onboarding.step1.body' | 'onboarding.step2.body' | 'onboarding.step3.body';
  accent: string;
}

const STEPS: Step[] = [
  {
    icon: '📥',
    titleKey: 'onboarding.step1.title',
    bodyKey: 'onboarding.step1.body',
    accent: '#2e6cb5',
  },
  {
    icon: '🎨',
    titleKey: 'onboarding.step2.title',
    bodyKey: 'onboarding.step2.body',
    accent: '#3e9a5e',
  },
  {
    icon: '✏️',
    titleKey: 'onboarding.step3.title',
    bodyKey: 'onboarding.step3.body',
    accent: '#8a4dbf',
  },
];

/**
 * First-visit modal walking through the upload → template → edit story.
 * Skippable. Persists dismissal in localStorage so returning users skip it.
 * Only rendered on the landing route (path === '/') to avoid surprising
 * users who deep-linked into /editor or /library.
 */
export function Onboarding() {
  const { t } = useT();
  const [step, setStep] = useState(0);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    if (window.location.pathname !== '/') return;
    if (localStorage.getItem(STORE_KEY) === '1') return;
    setShow(true);
  }, []);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const dismiss = () => {
    try {
      localStorage.setItem(STORE_KEY, '1');
    } catch {
      // ignore
    }
    setShow(false);
  };

  if (!show) return null;

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step]!;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/45 grid place-items-center px-4"
      data-testid="onboarding"
    >
      <div
        className="bg-paper border-[2.5px] border-ink rounded-modal w-full max-w-md p-6"
        style={{
          boxShadow:
            '0 1px 0 rgba(0,0,0,.04), 0 12px 28px rgba(60,40,20,.18), 0 30px 60px rgba(60,40,20,.18)',
        }}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="font-caveat text-ink-soft text-[14px]">
            {step + 1} / {STEPS.length}
          </span>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('generic.close')}
            className="text-ink-soft hover:text-ink text-[12px] underline-offset-2 hover:underline font-hand"
            data-testid="onboarding-skip"
          >
            {t('onboarding.skip')}
          </button>
        </div>

        <div
          className="flex flex-col items-center text-center gap-3 my-4"
          data-testid={`onboarding-step-${step + 1}`}
        >
          <div
            className="text-5xl grid place-items-center"
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `${current.accent}20`,
              border: `2.5px solid ${current.accent}`,
            }}
          >
            {current.icon}
          </div>
          <h2 className="font-gloock text-[22px] leading-tight" style={{ color: current.accent }}>
            {t(current.titleKey)}
          </h2>
          <p className="font-hand text-[15px] text-ink leading-[1.5]">{t(current.bodyKey)}</p>
        </div>

        <div className="flex items-center justify-between gap-3 mt-4">
          <div className="flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <span
                key={s.titleKey}
                className={[
                  'h-2 rounded-full transition-all',
                  i === step ? 'w-6 bg-ink' : 'w-2 bg-ink/30',
                ].join(' ')}
                aria-hidden
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => (isLast ? dismiss() : setStep((s) => s + 1))}
            className="px-4 py-2 bg-accent-yellow text-ink font-gloock text-[15px] border-[2.5px] border-ink rounded-control shadow-ink-sm hover:translate-y-[-1px] transition-transform"
            data-testid="onboarding-next"
          >
            {isLast ? t('onboarding.start') : t('onboarding.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
